/**
 * groupContext — shared state for Groups tab (posts, my groups, all groups).
 */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import groupService, {
  type FetchGroupsListParams,
} from '../api/services/groupService';
import { GROUP_STATUS, STORAGE_KEYS } from '../constants';
import { getReactionById } from '../constants/reactions';
import { analytics, ANALYTICS_EVENTS } from '../analytics';
import { mapGroupPostToFeed, enrichGroupPostsWithGroupImages, enrichGroupFeedPostsWithGroupImages } from '../utils/mapGroupPost';
import { useAuth } from './AuthContext';
import { eventBus } from '../platform/eventBus';
import { USER_SESSION_CLEARED_EVENT } from '../api/auth/clearUserSession';
import type {
  Group,
  GroupContextValue,
  GroupFeedMutation,
  GroupFeedPost,
  GroupFetchOptions,
  GroupState,
} from '../types';
import { getUserFacingErrorMessage } from '../utils/apiErrorHandler';

export const GROUP_MEMBERSHIP_CHANGED_EVENT = 'GROUP_MEMBERSHIP_CHANGED_EVENT';

const initialState: GroupState = {
  groupPosts: [],
  myGroups: [],
  allGroups: [],
  groupPostsLoading: false,
  myGroupsLoading: false,
  allGroupsLoading: false,
  groupPostsError: null,
  myGroupsError: null,
  allGroupsError: null,
  groupPostsLoaded: false,
  myGroupsLoaded: false,
  allGroupsLoaded: false,
  allGroupsTotalCount: 0,
  pinnedPostMap: {},
};

const GROUP_ACTIONS = {
  POSTS_LOADING: 'POSTS_LOADING',
  POSTS_SUCCESS: 'POSTS_SUCCESS',
  POSTS_ERROR: 'POSTS_ERROR',
  MY_GROUPS_LOADING: 'MY_GROUPS_LOADING',
  MY_GROUPS_SUCCESS: 'MY_GROUPS_SUCCESS',
  MY_GROUPS_ERROR: 'MY_GROUPS_ERROR',
  ALL_GROUPS_LOADING: 'ALL_GROUPS_LOADING',
  ALL_GROUPS_SUCCESS: 'ALL_GROUPS_SUCCESS',
  ALL_GROUPS_ERROR: 'ALL_GROUPS_ERROR',
  LIKE_TOGGLE: 'LIKE_TOGGLE',
  SAVE_TOGGLE: 'SAVE_TOGGLE',
  JOIN_GROUP: 'JOIN_GROUP',
  LEAVE_GROUP: 'LEAVE_GROUP',
  REMOVE_GROUP: 'REMOVE_GROUP',
  ADD_GROUP: 'ADD_GROUP',
  UPDATE_GROUP: 'UPDATE_GROUP',
  PREPEND_NEW_POSTS: 'PREPEND_NEW_POSTS',
  PIN_GROUP_POST: 'PIN_GROUP_POST',
  POST_UPDATE: 'POST_UPDATE',
  POST_DELETE: 'POST_DELETE',
  UPDATE_COMMENT_COUNT: 'UPDATE_COMMENT_COUNT',
  UPDATE_SHARE_COUNT: 'UPDATE_SHARE_COUNT',
  BLOCK_USER_TOGGLE: 'BLOCK_USER_TOGGLE',
  RESET: 'RESET',
} as const;

type GroupAction =
  | { type: typeof GROUP_ACTIONS.POSTS_LOADING }
  | { type: typeof GROUP_ACTIONS.POSTS_SUCCESS; posts: GroupFeedPost[] }
  | { type: typeof GROUP_ACTIONS.POSTS_ERROR; error: string }
  | { type: typeof GROUP_ACTIONS.MY_GROUPS_LOADING }
  | { type: typeof GROUP_ACTIONS.MY_GROUPS_SUCCESS; groups: Group[] }
  | { type: typeof GROUP_ACTIONS.MY_GROUPS_ERROR; error: string }
  | { type: typeof GROUP_ACTIONS.ALL_GROUPS_LOADING }
  | { type: typeof GROUP_ACTIONS.ALL_GROUPS_SUCCESS; groups: Group[]; totalCount?: number }
  | { type: typeof GROUP_ACTIONS.ALL_GROUPS_ERROR; error: string }
  | {
      type: typeof GROUP_ACTIONS.LIKE_TOGGLE;
      postId: string;
      liked?: boolean;
      nextLikes?: number;
      reactionTypeId?: number | null;
      reactionName?: string | null;
    }
  | { type: typeof GROUP_ACTIONS.SAVE_TOGGLE; postId: string }
  | {
      type: typeof GROUP_ACTIONS.JOIN_GROUP;
      groupId: string;
      status: Group['status'];
    }
  | { type: typeof GROUP_ACTIONS.LEAVE_GROUP; groupId: string }
  | { type: typeof GROUP_ACTIONS.REMOVE_GROUP; groupId: string }
  | { type: typeof GROUP_ACTIONS.ADD_GROUP; group: Group }
  | { type: typeof GROUP_ACTIONS.UPDATE_GROUP; group: Group }
  | { type: typeof GROUP_ACTIONS.PREPEND_NEW_POSTS; posts: GroupFeedPost[] }
  | {
      type: typeof GROUP_ACTIONS.PIN_GROUP_POST;
      groupId: string;
      postId: string | null;
    }
  | {
      type: typeof GROUP_ACTIONS.POST_UPDATE;
      postId: string;
      updates: Partial<GroupFeedPost>;
    }
  | { type: typeof GROUP_ACTIONS.POST_DELETE; postId: string }
  | {
      type: typeof GROUP_ACTIONS.UPDATE_COMMENT_COUNT;
      postId: string;
      delta: number;
    }
  | { type: typeof GROUP_ACTIONS.UPDATE_SHARE_COUNT; postId: string }
  | {
      type: typeof GROUP_ACTIONS.BLOCK_USER_TOGGLE;
      userId?: string | number | null;
      username?: string;
      isBlocked?: boolean;
    }
  | { type: typeof GROUP_ACTIONS.RESET };

const applyJoinStatus = (
  groups: Group[],
  groupId: string,
  status: Group['status'],
): Group[] =>
  groups.map(g => (g.id !== groupId ? g : { ...g, status }));

const upsertMyGroup = (
  myGroups: Group[],
  group: Group,
  status: Group['status'],
): Group[] => {
  const existing = myGroups.find(g => g.id === group.id);
  if (existing) {
    return applyJoinStatus(myGroups, group.id, status);
  }
  return [{ ...group, status }, ...myGroups];
};

const mergeGroupLists = (myGroups: Group[], allGroups: Group[]): Group[] => [
  ...myGroups,
  ...allGroups,
];

const groupReducer = (state: GroupState, action: GroupAction): GroupState => {
  switch (action.type) {
    case GROUP_ACTIONS.POSTS_LOADING:
      return { ...state, groupPostsLoading: true, groupPostsError: null };
    case GROUP_ACTIONS.POSTS_SUCCESS:
      return {
        ...state,
        groupPostsLoading: false,
        groupPosts: enrichGroupFeedPostsWithGroupImages(
          action.posts,
          mergeGroupLists(state.myGroups, state.allGroups),
        ),
        groupPostsError: null,
        groupPostsLoaded: true,
      };
    // `*Loaded` is untouched on failure so the screen keeps reporting the error
    // instead of falling through to its empty copy.
    case GROUP_ACTIONS.POSTS_ERROR:
      return { ...state, groupPostsLoading: false, groupPostsError: action.error || null };

    case GROUP_ACTIONS.MY_GROUPS_LOADING:
      return { ...state, myGroupsLoading: true, myGroupsError: null };
    case GROUP_ACTIONS.MY_GROUPS_SUCCESS: {
      const myGroups = action.groups;
      return {
        ...state,
        myGroupsLoading: false,
        myGroups,
        groupPosts: enrichGroupFeedPostsWithGroupImages(
          state.groupPosts,
          mergeGroupLists(myGroups, state.allGroups),
        ),
        myGroupsError: null,
        myGroupsLoaded: true,
      };
    }
    case GROUP_ACTIONS.MY_GROUPS_ERROR:
      return { ...state, myGroupsLoading: false, myGroupsError: action.error };

    case GROUP_ACTIONS.ALL_GROUPS_LOADING:
      return { ...state, allGroupsLoading: true, allGroupsError: null };
    case GROUP_ACTIONS.ALL_GROUPS_SUCCESS: {
      const allGroups = action.groups;
      return {
        ...state,
        allGroupsLoading: false,
        allGroups,
        groupPosts: enrichGroupFeedPostsWithGroupImages(
          state.groupPosts,
          mergeGroupLists(state.myGroups, allGroups),
        ),
        allGroupsError: null,
        allGroupsLoaded: true,
        allGroupsTotalCount: action.totalCount ?? allGroups.length,
      };
    }
    case GROUP_ACTIONS.ALL_GROUPS_ERROR:
      return { ...state, allGroupsLoading: false, allGroupsError: action.error };

    case GROUP_ACTIONS.LIKE_TOGGLE:
      return {
        ...state,
        groupPosts: state.groupPosts.map(post => {
          if (post.id !== action.postId) return post;
          const nextLiked = action.liked !== undefined ? action.liked : !post.liked;
          return {
            ...post,
            liked: nextLiked,
            likes:
              action.nextLikes !== undefined
                ? action.nextLikes
                : nextLiked
                ? (post.likes || 0) + 1
                : Math.max(0, (post.likes || 0) - 1),
            reactionTypeId:
              action.reactionTypeId !== undefined
                ? action.reactionTypeId
                : nextLiked
                ? (post.reactionTypeId ?? 1)
                : null,
            reactionName:
              action.reactionName !== undefined
                ? action.reactionName
                : nextLiked
                ? (post.reactionName ?? 'Like')
                : null,
          };
        }),
      };

    case GROUP_ACTIONS.SAVE_TOGGLE:
      return {
        ...state,
        groupPosts: state.groupPosts.map(post =>
          post.id !== action.postId ? post : { ...post, saved: !post.saved },
        ),
      };

    case GROUP_ACTIONS.JOIN_GROUP: {
      const group = state.allGroups.find(g => g.id === action.groupId);
      return {
        ...state,
        allGroups: applyJoinStatus(
          state.allGroups,
          action.groupId,
          action.status,
        ),
        myGroups: group
          ? upsertMyGroup(state.myGroups, group, action.status)
          : state.myGroups,
      };
    }

    case GROUP_ACTIONS.LEAVE_GROUP:
      return {
        ...state,
        myGroups: state.myGroups.filter(g => g.id !== action.groupId),
        allGroups: applyJoinStatus(
          state.allGroups,
          action.groupId,
          GROUP_STATUS.JOIN_NOW,
        ),
      };

    case GROUP_ACTIONS.REMOVE_GROUP:
      return {
        ...state,
        myGroups: state.myGroups.filter(g => g.id !== action.groupId),
        allGroups: state.allGroups.filter(g => g.id !== action.groupId),
      };

    case GROUP_ACTIONS.ADD_GROUP:
      return {
        ...state,
        myGroups: [
          action.group,
          ...state.myGroups.filter(g => g.id !== action.group.id),
        ],
        allGroups: state.allGroups.some(g => g.id === action.group.id)
          ? applyJoinStatus(
              state.allGroups,
              action.group.id,
              action.group.status,
            )
          : [action.group, ...state.allGroups],
      };

    case GROUP_ACTIONS.UPDATE_GROUP: {
      const mergeGroup = (g: Group): Group => {
        if (g.id !== action.group.id) return g;
        // UpdateGroup may return null icon/banner URLs even when images were
        // unchanged — do not wipe existing list images with null.
        // Trade-off: an intentional clear (user removed icon) also returns null,
        // so the list keeps the old image until a full refresh. If the API adds
        // a distinct cleared signal (e.g. empty string vs null), honour that here
        // instead of dropping all nulls.
        const patch: Partial<Group> = { ...action.group };
        if (patch.groupImage == null) delete patch.groupImage;
        if (patch.groupCoverImage == null) delete patch.groupCoverImage;
        return { ...g, ...patch };
      };
      return {
        ...state,
        myGroups: state.myGroups.map(mergeGroup),
        allGroups: state.allGroups.map(mergeGroup),
      };
    }

    case GROUP_ACTIONS.PREPEND_NEW_POSTS: {
      const existingIds = new Set(state.groupPosts.map(p => p.id));
      const newPosts = enrichGroupFeedPostsWithGroupImages(
        action.posts.filter(p => !existingIds.has(p.id)),
        mergeGroupLists(state.myGroups, state.allGroups),
      );
      return {
        ...state,
        groupPosts: [...newPosts, ...state.groupPosts],
      };
    }
    case GROUP_ACTIONS.PIN_GROUP_POST:
      return {
        ...state,
        pinnedPostMap: {
          ...state.pinnedPostMap,
          [action.groupId]: action.postId,
        },
      };

    case GROUP_ACTIONS.POST_UPDATE:
      return {
        ...state,
        groupPosts: state.groupPosts.map(post =>
          post.id !== action.postId ? post : { ...post, ...action.updates },
        ),
      };

    case GROUP_ACTIONS.POST_DELETE:
      return {
        ...state,
        groupPosts: state.groupPosts.filter(post => post.id !== action.postId),
      };

    case GROUP_ACTIONS.UPDATE_COMMENT_COUNT:
      return {
        ...state,
        groupPosts: state.groupPosts.map(post =>
          post.id !== action.postId
            ? post
            : {
                ...post,
                comments: Math.max(0, (post.comments || 0) + action.delta),
              },
        ),
      };

    case GROUP_ACTIONS.UPDATE_SHARE_COUNT:
      return {
        ...state,
        groupPosts: state.groupPosts.map(post =>
          String(post.id) !== String(action.postId)
            ? post
            : { ...post, shares: (post.shares || 0) + 1 },
        ),
      };

    case GROUP_ACTIONS.BLOCK_USER_TOGGLE:
      return {
        ...state,
        groupPosts: state.groupPosts.map(post => {
          const matchUser =
            (action.userId && String(post.userId) === String(action.userId)) ||
            (action.username && post.username === action.username);
          if (!matchUser) return post;
          return {
            ...post,
            isBlocked:
              typeof action.isBlocked === 'boolean'
                ? action.isBlocked
                : !post.isBlocked,
          };
        }),
      };

    case GROUP_ACTIONS.RESET:
      return { ...initialState };

    default:
      return state;
  }
};

const groupContext = createContext<GroupContextValue | null>(null);

export const GroupProvider = ({
  children,
}: PropsWithChildren): React.JSX.Element => {
  const [state, dispatch] = useReducer(groupReducer, initialState);
  const { isAuthenticated } = useAuth();
  const activeFetchId = useRef(0);
  const stateRef = useRef(state);
  stateRef.current = state;

  /** Cache-first keys so tab remounts do not refetch or flash loaders. */
  const postsCacheKeyRef = useRef<string | null>(null);
  const myGroupsLoadedRef = useRef(false);
  const allGroupsCacheKeyRef = useRef<string | null>(null);
  const lastMyGroupsParamsRef = useRef<FetchGroupsListParams | undefined>(undefined);
  const lastAllGroupsParamsRef = useRef<FetchGroupsListParams | undefined>(undefined);

  const [groupFeedMutationVersion, setGroupFeedMutationVersion] = useState(0);
  const [lastGroupFeedMutation, setLastGroupFeedMutation] =
    useState<GroupFeedMutation | null>(null);

  const resetGroupCaches = useCallback(() => {
    activeFetchId.current += 1;
    postsCacheKeyRef.current = null;
    myGroupsLoadedRef.current = false;
    allGroupsCacheKeyRef.current = null;
    lastMyGroupsParamsRef.current = undefined;
    lastAllGroupsParamsRef.current = undefined;
    setLastGroupFeedMutation(null);
    setGroupFeedMutationVersion(0);
    dispatch({ type: GROUP_ACTIONS.RESET });
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      resetGroupCaches();
    }
  }, [isAuthenticated, resetGroupCaches]);

  useEffect(() => {
    const sub = eventBus.addListener(USER_SESSION_CLEARED_EVENT, () => {
      resetGroupCaches();
    });
    return () => sub.remove();
  }, [resetGroupCaches]);

  const emitGroupFeedMutation = useCallback((mutation: GroupFeedMutation) => {
    setLastGroupFeedMutation(mutation);
    setGroupFeedMutationVersion(v => v + 1);
  }, []);

  /** Prefetch group lists so home-feed post icons can resolve on first load. */
  const ensureGroupListCache = useCallback(async (): Promise<Group[]> => {
    let fetchedMyGroups: Group[] | null = null;
    let fetchedAllGroups: Group[] | null = null;
    const tasks: Promise<void>[] = [];

    if (!myGroupsLoadedRef.current) {
      tasks.push(
        groupService
          .getMyGroups()
          .then(response => {
            myGroupsLoadedRef.current = true;
            fetchedMyGroups = response.data;
            dispatch({
              type: GROUP_ACTIONS.MY_GROUPS_SUCCESS,
              groups: response.data,
            });
          })
          .catch(() => undefined),
      );
    }

    if (!allGroupsCacheKeyRef.current) {
      tasks.push(
        groupService
          .getAllGroups()
          .then(response => {
            // Must match the key fetchAllGroups builds for "no params".
            allGroupsCacheKeyRef.current = '||';
            fetchedAllGroups = response.data;
            dispatch({
              type: GROUP_ACTIONS.ALL_GROUPS_SUCCESS,
              groups: response.data,
              totalCount: response.totalCount,
            });
          })
          .catch(() => undefined),
      );
    }

    if (tasks.length > 0) {
      await Promise.all(tasks);
    }

    const myGroups = fetchedMyGroups ?? stateRef.current.myGroups;
    const allGroups = fetchedAllGroups ?? stateRef.current.allGroups;
    return mergeGroupLists(myGroups, allGroups);
  }, []);

  const fetchGroupPosts = useCallback(async (
    groupId: string | null = null,
    options?: GroupFetchOptions,
  ) => {
    const cacheKey = groupId ?? 'home';
    if (!options?.force && postsCacheKeyRef.current === cacheKey) {
      // Already loaded for this scope — preserve list, no spinner.
      return;
    }

    const fetchId = ++activeFetchId.current;
    const hasCached =
      postsCacheKeyRef.current === cacheKey &&
      stateRef.current.groupPosts.length > 0;

    // First load: full loader. Pull-to-refresh: refreshing indicator only.
    // Background refresh (silent): do not toggle loading indicator.
    const shouldSetLoading = !options?.silent && (!hasCached || options?.force);
    if (shouldSetLoading) {
      dispatch({ type: GROUP_ACTIONS.POSTS_LOADING });
    }

    try {
      const cachedGroupsPromise = !groupId ? ensureGroupListCache() : Promise.resolve([] as Group[]);
      const [groupPostsResponse, cachedGroupsForHome] = await Promise.all([
        groupService.getGroupPosts(groupId ?? undefined),
        cachedGroupsPromise,
      ]);
      if (activeFetchId.current !== fetchId) {
        if (shouldSetLoading) {
          dispatch({ type: GROUP_ACTIONS.POSTS_ERROR, error: '' });
        }
        return;
      }
      const cachedGroups = groupId
        ? [...stateRef.current.myGroups, ...stateRef.current.allGroups]
        : cachedGroupsForHome;
      const enrichedPosts = enrichGroupPostsWithGroupImages(
        groupPostsResponse.data,
        cachedGroups,
      );
      const posts = enrichedPosts.map(mapGroupPostToFeed);
      postsCacheKeyRef.current = cacheKey;
      dispatch({ type: GROUP_ACTIONS.POSTS_SUCCESS, posts });
    } catch (err) {
      if (activeFetchId.current !== fetchId) {
        if (shouldSetLoading) {
          dispatch({ type: GROUP_ACTIONS.POSTS_ERROR, error: '' });
        }
        return;
      }
      dispatch({
        type: GROUP_ACTIONS.POSTS_ERROR,
        error: getUserFacingErrorMessage(err, 'Failed to load group posts'),
      });
    }
  }, [ensureGroupListCache]);

  const fetchMyGroups = useCallback(async (
    params?: FetchGroupsListParams,
    options?: GroupFetchOptions,
  ) => {
    if (params !== undefined) {
      lastMyGroupsParamsRef.current = params;
    }
    if (!options?.force && myGroupsLoadedRef.current) {
      return;
    }

    if (!options?.silent) {
      dispatch({ type: GROUP_ACTIONS.MY_GROUPS_LOADING });
    }

    try {
      const myGroupsResponse = await groupService.getMyGroups(params);
      myGroupsLoadedRef.current = true;
      dispatch({
        type: GROUP_ACTIONS.MY_GROUPS_SUCCESS,
        groups: myGroupsResponse.data,
      });
    } catch (err) {
      dispatch({
        type: GROUP_ACTIONS.MY_GROUPS_ERROR,
        error: getUserFacingErrorMessage(err, 'Failed to load my groups'),
      });
    }
  }, []);

  const fetchAllGroups = useCallback(async (
    params?: FetchGroupsListParams,
    options?: GroupFetchOptions,
  ) => {
    if (params !== undefined) {
      lastAllGroupsParamsRef.current = params;
    }
    const cacheKey = `${params?.sortBy ?? ''}|${params?.searchTerm ?? ''}|${params?.groupType ?? ''}`;
    if (!options?.force && allGroupsCacheKeyRef.current === cacheKey) {
      return;
    }

    if (!options?.silent) {
      dispatch({ type: GROUP_ACTIONS.ALL_GROUPS_LOADING });
    }

    try {
      const allGroupsResponse = await groupService.getAllGroups(params);
      allGroupsCacheKeyRef.current = cacheKey;
      dispatch({
        type: GROUP_ACTIONS.ALL_GROUPS_SUCCESS,
        groups: allGroupsResponse.data,
        totalCount: allGroupsResponse.totalCount,
      });
    } catch (err) {
      dispatch({
        type: GROUP_ACTIONS.ALL_GROUPS_ERROR,
        error: getUserFacingErrorMessage(err, 'Failed to load groups'),
      });
    }
  }, []);

  const likeGroupPost = useCallback(async (postId: string, reactionTypeId?: number) => {
    const post = stateRef.current.groupPosts.find(p => p.id === postId);
    const currentlyLiked = post?.liked ?? false;
    const currentReactionTypeId = post?.reactionTypeId ?? (currentlyLiked ? 1 : null);
    const currentReactionName = post?.reactionName ?? (currentlyLiked ? 'Like' : null);
    const currentLikes = post?.likes ?? 0;

    let nextLiked: boolean;
    let nextLikes: number;
    let nextReactionTypeId: number | null;
    let nextReactionName: string | null;

    if (reactionTypeId != null && reactionTypeId > 0) {
      const def = getReactionById(reactionTypeId);
      if (currentlyLiked && currentReactionTypeId === reactionTypeId) {
        // Toggle off (unlike)
        nextLiked = false;
        nextLikes = Math.max(0, currentLikes - 1);
        nextReactionTypeId = null;
        nextReactionName = null;
      } else if (currentlyLiked) {
        // Switch reaction (LinkedIn style - like count unchanged)
        nextLiked = true;
        nextLikes = currentLikes;
        nextReactionTypeId = reactionTypeId;
        nextReactionName = def?.name ?? 'Like';
      } else {
        // New reaction
        nextLiked = true;
        nextLikes = currentLikes + 1;
        nextReactionTypeId = reactionTypeId;
        nextReactionName = def?.name ?? 'Like';
      }
    } else {
      nextLiked = !currentlyLiked;
      nextLikes = nextLiked ? currentLikes + 1 : Math.max(0, currentLikes - 1);
      nextReactionTypeId = nextLiked ? 1 : null;
      nextReactionName = nextLiked ? 'Like' : null;
    }

    dispatch({
      type: GROUP_ACTIONS.LIKE_TOGGLE,
      postId,
      liked: nextLiked,
      nextLikes,
      reactionTypeId: nextReactionTypeId,
      reactionName: nextReactionName,
    });
    emitGroupFeedMutation({
      type: 'like',
      postId,
      liked: nextLiked,
      nextLikes,
      reactionTypeId: nextReactionTypeId,
      reactionName: nextReactionName,
    });
    try {
      const targetReactionTypeId = nextLiked
        ? (nextReactionTypeId ?? 1)
        : (currentReactionTypeId ?? reactionTypeId ?? 1);
      await groupService.likeGroupPost(postId, targetReactionTypeId);
      if (!currentlyLiked) {
        analytics.trackEvent('post_liked', { source: 'group' });
        analytics.trackOnce(
          STORAGE_KEYS.ANALYTICS_FIRST_LIKE,
          ANALYTICS_EVENTS.ACTIVATION_FIRST_LIKE,
        );
      }
    } catch {
      dispatch({
        type: GROUP_ACTIONS.LIKE_TOGGLE,
        postId,
        liked: currentlyLiked,
        nextLikes: currentLikes,
        reactionTypeId: currentReactionTypeId,
        reactionName: currentReactionName,
      });
      emitGroupFeedMutation({
        type: 'like',
        postId,
        liked: currentlyLiked,
        nextLikes: currentLikes,
        reactionTypeId: currentReactionTypeId,
        reactionName: currentReactionName,
      });
    }
  }, [emitGroupFeedMutation]);

  const saveGroupPost = useCallback(async (postId: string) => {
    const post = stateRef.current.groupPosts.find(p => p.id === postId);
    const isSave = post ? !post.saved : true;
    dispatch({ type: GROUP_ACTIONS.SAVE_TOGGLE, postId });
    emitGroupFeedMutation({ type: 'save', postId });
    try {
      await groupService.saveGroupPost(postId, isSave);
    } catch {
      dispatch({ type: GROUP_ACTIONS.SAVE_TOGGLE, postId });
      emitGroupFeedMutation({ type: 'save', postId });
    }
  }, [emitGroupFeedMutation]);

  const updateGroupCommentCount = useCallback(
    (postId: string, delta: number) => {
      dispatch({ type: GROUP_ACTIONS.UPDATE_COMMENT_COUNT, postId, delta });
      emitGroupFeedMutation({ type: 'comment', postId, delta });
    },
    [emitGroupFeedMutation],
  );

  const updateGroupShareCount = useCallback(
    (postId: string) => {
      const normalizedPostId = String(postId);
      dispatch({ type: GROUP_ACTIONS.UPDATE_SHARE_COUNT, postId: normalizedPostId });
      emitGroupFeedMutation({ type: 'share', postId: normalizedPostId });
    },
    [emitGroupFeedMutation],
  );

  const toggleGroupBlockUser = useCallback(
    (
      userId?: string | number | null,
      username?: string,
      isBlocked?: boolean,
    ) => {
      dispatch({
        type: GROUP_ACTIONS.BLOCK_USER_TOGGLE,
        userId,
        username,
        isBlocked,
      });
      emitGroupFeedMutation({ type: 'block', userId, username, isBlocked });
    },
    [emitGroupFeedMutation],
  );

  const refreshGroupFeeds = useCallback(async () => {
    try {
      await Promise.all([
        fetchMyGroups(lastMyGroupsParamsRef.current, { force: true, silent: true }),
        fetchGroupPosts(undefined, { force: true, silent: true }),
        fetchAllGroups(lastAllGroupsParamsRef.current, { force: true, silent: true }),
      ]);
    } catch (err) {
      console.warn('[GroupContext] Error refreshing group feeds:', err);
    }
  }, [fetchMyGroups, fetchGroupPosts, fetchAllGroups]);

  const joinGroup = useCallback(
    async (groupId: string, fallbackGroup?: Group) => {
      const group =
        state.allGroups.find(g => g.id === groupId) ??
        state.myGroups.find(g => g.id === groupId) ??
        fallbackGroup;
      if (!group) return;

      const wasPending = group.status === GROUP_STATUS.PENDING;
      const previousStatus = group.status;

      if (wasPending) {
        dispatch({
          type: GROUP_ACTIONS.JOIN_GROUP,
          groupId,
          status: GROUP_STATUS.JOIN_NOW,
        });
        try {
          await groupService.cancelJoinRequest(groupId);
          await refreshGroupFeeds();
          eventBus.emit(GROUP_MEMBERSHIP_CHANGED_EVENT, { groupId, action: 'cancelJoin' });
        } catch {
          dispatch({
            type: GROUP_ACTIONS.JOIN_GROUP,
            groupId,
            status: previousStatus,
          });
        }
        return;
      }

      const optimisticStatus =
        group.requireJoinApproval === true
          ? GROUP_STATUS.PENDING
          : GROUP_STATUS.JOINED;

      dispatch({
        type: GROUP_ACTIONS.JOIN_GROUP,
        groupId,
        status: optimisticStatus,
      });

      try {
        const result = await groupService.joinGroup(groupId, {
          requireJoinApproval: group.requireJoinApproval,
        });
        dispatch({
          type: GROUP_ACTIONS.JOIN_GROUP,
          groupId,
          status: result.status,
        });
        await refreshGroupFeeds();
        eventBus.emit(GROUP_MEMBERSHIP_CHANGED_EVENT, { groupId, action: 'join' });
      } catch {
        dispatch({
          type: GROUP_ACTIONS.JOIN_GROUP,
          groupId,
          status: previousStatus,
        });
      }
    },
    [state.allGroups, state.myGroups, refreshGroupFeeds],
  );

  const leaveGroup = useCallback(
    async (groupId: string) => {
      await groupService.leaveGroup(groupId);
      dispatch({ type: GROUP_ACTIONS.LEAVE_GROUP, groupId });
      await refreshGroupFeeds();
      eventBus.emit(GROUP_MEMBERSHIP_CHANGED_EVENT, { groupId, action: 'leave' });
    },
    [refreshGroupFeeds],
  );

  const deleteGroup = useCallback(
    async (groupId: string) => {
      await groupService.deleteGroup(groupId);
      dispatch({ type: GROUP_ACTIONS.REMOVE_GROUP, groupId });
      await refreshGroupFeeds();
      eventBus.emit(GROUP_MEMBERSHIP_CHANGED_EVENT, { groupId, action: 'delete' });
    },
    [refreshGroupFeeds],
  );

  const addCreatedGroup = useCallback(
    (group: Group) => {
      dispatch({ type: GROUP_ACTIONS.ADD_GROUP, group });
      refreshGroupFeeds().catch(() => {});
      eventBus.emit(GROUP_MEMBERSHIP_CHANGED_EVENT, { groupId: group.id, action: 'create' });
    },
    [refreshGroupFeeds],
  );

  const updateGroupInState = useCallback((group: Group) => {
    dispatch({ type: GROUP_ACTIONS.UPDATE_GROUP, group });
  }, []);

  const syncNewGroupPosts = useCallback(async (groupId: string) => {
    try {
      const groupPostsResponse = await groupService.getGroupPosts(groupId);
      const cachedGroups = [
        ...stateRef.current.myGroups,
        ...stateRef.current.allGroups,
      ];
      const enrichedPosts = enrichGroupPostsWithGroupImages(
        groupPostsResponse.data,
        cachedGroups,
      );
      const posts = enrichedPosts.map(mapGroupPostToFeed);
      postsCacheKeyRef.current = groupId;
      dispatch({ type: GROUP_ACTIONS.PREPEND_NEW_POSTS, posts });
    } catch (err) {
      console.error('Failed to sync new group posts:', err);
    }
  }, []);

  const prependGroupPosts = useCallback((posts: GroupFeedPost[]) => {
    if (!posts.length) return;
    dispatch({ type: GROUP_ACTIONS.PREPEND_NEW_POSTS, posts });
    emitGroupFeedMutation({ type: 'prepend', posts });
  }, [emitGroupFeedMutation]);

  const updateGroupPost = useCallback(
    (postId: string, updates: Partial<GroupFeedPost>) => {
      dispatch({ type: GROUP_ACTIONS.POST_UPDATE, postId, updates });
      emitGroupFeedMutation({ type: 'update', postId, updates });
    },
    [emitGroupFeedMutation],
  );

  const deleteGroupPost = useCallback((postId: string) => {
    dispatch({ type: GROUP_ACTIONS.POST_DELETE, postId });
    emitGroupFeedMutation({ type: 'delete', postId });
  }, [emitGroupFeedMutation]);
  
  const pinGroupPost = useCallback(
    (groupId: string, postId: string | null) => {
      dispatch({
        type: GROUP_ACTIONS.PIN_GROUP_POST,
        groupId,
        postId,
      });
    },
    [],
  );

  return (
    <groupContext.Provider
      value={{
        ...state,
        fetchGroupPosts,
        fetchMyGroups,
        fetchAllGroups,
        likeGroupPost,
        saveGroupPost,
        updateGroupCommentCount,
        updateGroupShareCount,
        toggleGroupBlockUser,
        joinGroup,
        leaveGroup,
        deleteGroup,
        addCreatedGroup,
        updateGroupInState,
        syncNewGroupPosts,
        prependGroupPosts,
        updateGroupPost,
        deleteGroupPost,
        pinGroupPost,
        groupFeedMutationVersion,
        lastGroupFeedMutation,
      }}>
      {children}
    </groupContext.Provider>
  );
};

export const useGroup = (): GroupContextValue => {
  const group = useContext(groupContext);
  if (!group) {
    throw new Error('useGroup must be used within a <GroupProvider>');
  }
  return group;
};

export default groupContext;
